/**
 * Integrytas Group - Dynamic Feature Flags (DFF)
 * Page: DFF.FLAGS
 *
 * Centralized switchboard: define flags + ordered targeting rules (workspace /
 * role / user / screen / canary %), and the endpoint guards the backend uses to
 * protect or reroute API paths. Supersedes the legacy ffm_ flags page.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DFF.FLAGS';

registerUdcHost('DFF.FLAGS', 'dff_flag', 'Feature Flags');

interface FlagRow { dff_flag_id: number; internal_code: string; name: string; value_type: string; default_value: unknown; is_active: 'Y' | 'N' }
interface RuleRow { dff_rule_id: number; priority: number; target_workspace_id: number | null; target_role_id: number | null; target_user_id: number | null; target_screen_code: string | null; rollout_pct: number | null; value: unknown; is_active: 'Y' | 'N' }
interface GuardRow { dff_endpoint_guard_id: number; flag_code: string; match_type: string; match_value: string; action: string; reroute_to: string | null; is_active: 'Y' | 'N' }
interface RoleLite { role_id: number; internal_code: string }
interface WsLite { une_workspace_id: number; name: string }

const FLAG_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 2 },
  { key: 'name', type: 'text', label: 'Name', col_span: 2 },
  { key: 'type', type: 'select', label: 'Type', col_span: 1 },
  { key: 'def', type: 'text', label: 'Default (JSON)', col_span: 2 },
  { key: 'active', type: 'boolean', label: 'Active', col_span: 1 },
];
const FLAG_CORE_LAYOUT = toUldCoreLayout(FLAG_FIELD_DEFS);

const FLAG_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',    defaultWidth: 220 },
  { key: 'name',          header: 'Name',    defaultWidth: 200 },
  { key: 'value_type',    header: 'Type',    defaultWidth: 100 },
  { key: 'default_value', header: 'Default', defaultWidth: 160 },
  { key: 'is_active',     header: 'Active',  defaultWidth: 90  },
];

export interface DffFlagsPageProps { session: AuthSession }

export function DffFlagsPage({ session }: DffFlagsPageProps) {
  return (
    <PersonalizationProvider pageCode="DFF.FLAGS" session={session}>
      <DffFlagsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DffFlagsPageInner({ session }: DffFlagsPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [flags, setFlags] = useState<FlagRow[]>([]);
  const [guards, setGuards] = useState<GuardRow[]>([]);
  const [roles, setRoles] = useState<RoleLite[]>([]);
  const [ws, setWs] = useState<WsLite[]>([]);
  const [editing, setEditing] = useState<FlagRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ type: string[]; active: string[] }>({ type: [], active: ['Y'] });
  const udcGrid = useUdcGrid('dff_flag', 'DFF.FLAGS', { pkColumn: 'dff_flag_id', rows: flags as unknown as Record<string, unknown>[] });

  // §17.B13: related-children expand/contract. A flag's endpoint guards are
  // already loaded at page level (they reference the flag by internal_code).
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const flag = flags.find(fl => fl.dff_flag_id === id);
    const code = flag?.internal_code ?? '';
    return [
      {
        key: 'guards',
        table: 'dff_endpoint_guard', pkColumn: 'dff_endpoint_guard_id',
        title: t('dff.related.guards', 'Endpoint Guards'),
        items: guards
          .filter(g => g.flag_code === code)
          .map(g => ({
            id: g.dff_endpoint_guard_id,
            label: `${g.match_type}: ${g.match_value || '—'}`,
            title: g.action + (g.reroute_to ? ` → ${g.reroute_to}` : ''),
          })),
      },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [f, g, r, w] = await Promise.all([
        apiDb.from('dff_flag').select('dff_flag_id, internal_code, name, value_type, default_value, is_active').order('internal_code'),
        apiDb.from('dff_endpoint_guard').select('dff_endpoint_guard_id, flag_code, match_type, match_value, action, reroute_to, is_active').order('dff_endpoint_guard_id'),
        apiDb.from('csm_role_master').select('role_id, internal_code').order('internal_code'),
        apiDb.from('une_workspace').select('une_workspace_id, name').eq('is_active', 'Y').order('name'),
      ]);
      if (f.error) throw f.error;
      setFlags((f.data ?? []) as FlagRow[]); setGuards((g.data ?? []) as GuardRow[]); setRoles((r.data ?? []) as RoleLite[]); setWs((w.data ?? []) as WsLite[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      type:   uniq(flags.map(fl => fl.value_type)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }],
    };
  }, [flags, t]);

  const facetsActive = facets.type.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ type: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // OR within a facet, AND across facets.
    const matchesFacets = (fl: FlagRow) =>
      (facets.type.length   === 0 || facets.type.includes(fl.value_type)) &&
      (facets.active.length === 0 || facets.active.includes(fl.is_active));
    return flags.filter(fl =>
      (!q || fl.internal_code.toLowerCase().includes(q) || (fl.name ?? '').toLowerCase().includes(q))
      && udcGrid.matchRow(fl as unknown as Record<string, unknown>)
      && matchesFacets(fl),
    );
  }, [flags, filter, facets, udcGrid]);

  const addGuard = async () => { await routedInsert(session, 'dff_endpoint_guard', { flag_code: flags[0]?.internal_code ?? '', match_type: 'RPC', match_value: '', action: 'BLOCK', is_active: 'Y' }); await reload(); };
  const saveGuard = async (g: GuardRow) => { await routedUpdate(session, 'dff_endpoint_guard', 'dff_endpoint_guard_id', g.dff_endpoint_guard_id, { flag_code: g.flag_code, match_type: g.match_type, match_value: g.match_value, action: g.action, reroute_to: g.reroute_to || null, is_active: g.is_active }); };
  const delGuard = async (id: number) => { await routedDelete(session, 'dff_endpoint_guard', 'dff_endpoint_guard_id', id); setGuards(gs => gs.filter(x => x.dff_endpoint_guard_id !== id)); };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('dff.title', 'Feature Flags')}
          description={t('dff.list.description', 'Define flags, targeting rules and the backend endpoint guards that gate API paths.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('dff.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('dff.new_flag', 'New Flag')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: flags.length, label: t('dff.stat.total', 'Total flags') },
            { value: flags.filter(fl => fl.is_active === 'Y').length, label: t('dff.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('dff.load_failed', "Couldn't load feature flags.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('dff.filter_placeholder', 'Filter flags…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('dff.col.type', 'Type')}     options={facetOptions.type}   selected={facets.type}   onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('dff.col.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('dff.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...FLAG_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('dff.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('dff.col.edit', 'Edit')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(fl => (
                  <Fragment key={fl.dff_flag_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(fl.dff_flag_id)}
                        hasChildren={hasRelated(fl.dff_flag_id)}
                        onToggle={() => detail.toggle(fl.dff_flag_id)}
                        expandLabel={t('dff.related.expand', 'Show related records')}
                        collapseLabel={t('dff.related.collapse', 'Hide related')}
                        tooltip={t('dff.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono">{fl.internal_code}</span>}
                        {col.key === 'name'          && fl.name}
                        {col.key === 'value_type'    && <span className="text-csm-xs">{fl.value_type}</span>}
                        {col.key === 'default_value' && <span className="font-mono text-csm-2xs">{JSON.stringify(fl.default_value)}</span>}
                        {col.key === 'is_active'     && <YesNoBadge value={fl.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(fl as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">{canChange && (<button onClick={e => { e.stopPropagation(); setEditing(fl); }} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('dff.action.edit', 'Edit')}</button>)}</td>
                  </tr>
                  {detail.isOpen(fl.dff_flag_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(fl.dff_flag_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('dff.empty', 'No feature flags')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('dff.new_flag_plus', '+ New Flag')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>

        <Section title={t('dff.section.endpoint_guards', 'Backend Endpoint Guards')} onAdd={addGuard}>
          {guards.map((g, i) => (
            <div key={g.dff_endpoint_guard_id} className="mb-1 flex flex-wrap items-center gap-1">
              <div className="w-44">
                <FilteredModalSingleSelect
                  label={t('dff.field.flag_code', 'Flag')}
                  options={flags.map(fl => ({ internal_code: fl.internal_code, display_text: fl.internal_code }))}
                  selectedCode={g.flag_code || null}
                  onChange={(code) => {
                    const v = code ?? '';
                    setGuards(gs => gs.map((x, j) => j === i ? { ...x, flag_code: v } : x));
                    void saveGuard({ ...guards[i], flag_code: v });
                  }}
                  allowClear={false}
                />
              </div>
              <select value={g.match_type} onChange={e => setGuards(gs => gs.map((x, j) => j === i ? { ...x, match_type: e.target.value } : x))} onBlur={() => void saveGuard(guards[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{['RPC', 'TABLE', 'KIND'].map(t => <option key={t} value={t}>{t}</option>)}</select>
              <input value={g.match_value} onChange={e => setGuards(gs => gs.map((x, j) => j === i ? { ...x, match_value: e.target.value } : x))} onBlur={() => void saveGuard(guards[i])} placeholder={t('dff.placeholder.match_value', 'rpc / table / kind')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <select value={g.action} onChange={e => setGuards(gs => gs.map((x, j) => j === i ? { ...x, action: e.target.value } : x))} onBlur={() => void saveGuard(guards[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{['BLOCK', 'REROUTE'].map(a => <option key={a} value={a}>{a}</option>)}</select>
              {g.action === 'REROUTE' && <input value={g.reroute_to ?? ''} onChange={e => setGuards(gs => gs.map((x, j) => j === i ? { ...x, reroute_to: e.target.value } : x))} onBlur={() => void saveGuard(guards[i])} placeholder={t('dff.placeholder.reroute_to', 'reroute to')} className="h-7 w-32 rounded border border-csm-border px-1 font-mono text-csm-2xs" />}
              <label className="flex items-center gap-1 text-csm-2xs"><input type="checkbox" checked={g.is_active === 'Y'} onChange={e => { setGuards(gs => gs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveGuard(guards[i])} /> {t('dff.on', 'on')}</label>
              <button onClick={() => void delGuard(g.dff_endpoint_guard_id)} className="text-csm-danger text-csm-2xs">{t('dff.del', 'del')}</button>
            </div>
          ))}
        </Section>
      </div>
      {(editing || creating) && (
        <FlagModal session={session} row={editing} roles={roles} ws={ws}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function FlagModal({ session, row, roles, ws, onClose, onSaved }: { session: AuthSession; row: FlagRow | null; roles: RoleLite[]; ws: WsLite[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const id = row?.dff_flag_id;

  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    type: row?.value_type ?? 'BOOL',
    def: JSON.stringify(row?.default_value ?? false),
    active: row ? row.is_active === 'Y' : false,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  // Custom fields placed on this entity (host = the list page) become first-class
  // FieldRenderer items: they interleave with native fields and inherit the same
  // per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('dff_flag', 'DFF.FLAGS', { pkColumn: 'dff_flag_id', pk: row?.dff_flag_id });
  const flagCoreLayout = useMemo(() => [...FLAG_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const flagFieldDefs = useMemo(() => [...FLAG_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('DFF.FLAGS.MODAL', flagCoreLayout);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [ruleValues, setRuleValues] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { reset(initialValues); }, [row?.dff_flag_id]); // eslint-disable-line

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const loadRules = async () => {
    if (!id) return;
    const { data } = await apiDb.from('dff_rule').select('dff_rule_id, priority, target_workspace_id, target_role_id, target_user_id, target_screen_code, rollout_pct, value, is_active').eq('dff_flag_id', id).order('priority');
    const loaded = (data ?? []) as RuleRow[];
    setRules(loaded);
    const init: Record<number, string> = {};
    loaded.forEach(r => { init[r.dff_rule_id] = JSON.stringify(r.value); });
    setRuleValues(init);
  };
  // loadRules is re-created each render; reload only when the selected id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadRules(); }, [id]);

  const saveFlag = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('dff.err.code_required', 'Code is required.'));
      let dv: unknown; try { dv = JSON.parse(form.def || 'false'); } catch { throw new Error(t('dff.err.default_json', 'Default value must be valid JSON.')); }
      const payload = { name: form.name.trim() || form.code.trim(), value_type: form.type, default_value: dv, is_active: form.active ? 'Y' : 'N' };
      if (isCreate) { const { error: e } = await routedInsert(session, 'dff_flag', { internal_code: form.code.trim().toUpperCase(), ...payload }); if (e) throw e; }
      else { const { error: e } = await routedUpdate(session, 'dff_flag', 'dff_flag_id', id, payload); if (e) throw e; }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addRule = async () => { if (!id) return; await routedInsert(session, 'dff_rule', { dff_flag_id: id, priority: (rules.length + 1) * 10, value: JSON.parse(form.def || 'true'), is_active: 'Y' }); await loadRules(); };
  const saveRule = async (r: RuleRow, valText: string) => {
    let v: unknown; try { v = JSON.parse(valText); } catch { setError(t('dff.err.rule_value_json', 'Rule value must be valid JSON.')); return; }
    await routedUpdate(session, 'dff_rule', 'dff_rule_id', r.dff_rule_id, { priority: r.priority, target_workspace_id: r.target_workspace_id, target_role_id: r.target_role_id, target_user_id: r.target_user_id, target_screen_code: r.target_screen_code || null, rollout_pct: r.rollout_pct, value: v, is_active: r.is_active });
  };
  const delRule = async (rid: number) => { await routedDelete(session, 'dff_rule', 'dff_rule_id', rid); setRules(rs => rs.filter(x => x.dff_rule_id !== rid)); };
  const patch = (i: number, p: Partial<RuleRow>) => setRules(rs => rs.map((x, j) => j === i ? { ...x, ...p } : x));

  return (
    <>
    <Modal
      title={isCreate ? t('dff.modal.create_title', 'Create Flag') : t('dff.modal.edit_title', 'Edit Flag — {{code}}', { code: row?.internal_code ?? '' })}
      onClose={handleClose}
      width="4xl"
      headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
      footer={
        <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('dff.close', 'Close')}</button>
      }
    >
      <div className="mb-3">
      <FieldRenderer
        fields={flagFieldDefs}
        layout={layout}
        columns={4}
        pageCode="DFF.FLAGS.MODAL"
        session={session}
        render={(fld) => {
          if (fld.key === 'code') return (
            <PersonalizedField componentKey="modal" fieldKey="code" label={t('dff.field.code', 'Code')}>
            <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dff.field.code', 'Code')}</label>
              <input value={form.code} disabled={!isCreate} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                onBlur={() => {
                  if (isCreate && form.code && !/^[A-Z][A-Z0-9_.]*$/.test(form.code)) {
                    setInternalCodeError(t('dff.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                  } else { setInternalCodeError(null); }
                }}
                className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
              {internalCodeError
                ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('dff.hint.code', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
              }
            </div>
            </PersonalizedField>
          );
          if (fld.key === 'name') return (
            <PersonalizedField componentKey="modal" fieldKey="name" label={t('dff.field.name', 'Name')}>
            <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dff.field.name', 'Name')}</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></div>
            </PersonalizedField>
          );
          if (fld.key === 'type') return (
            <PersonalizedField componentKey="modal" fieldKey="type" label={t('dff.field.type', 'Type')}>
            <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dff.field.type', 'Type')}</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{['BOOL', 'STRING', 'NUMBER', 'JSON'].map(t => <option key={t} value={t}>{t}</option>)}</select></div>
            </PersonalizedField>
          );
          if (fld.key === 'def') return (
            <PersonalizedField componentKey="modal" fieldKey="def" label={t('dff.field.default_json', 'Default (JSON)')}>
            <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dff.field.default_json', 'Default (JSON)')}</label>
              <input value={form.def} onChange={e => setForm({ ...form, def: e.target.value })} placeholder="false" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></div>
            </PersonalizedField>
          );
          if (fld.key === 'active') return (
            <PersonalizedField componentKey="modal" fieldKey="active" label={t('dff.field.active', 'Active')}>
            <div className="flex h-full items-end"><label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-gray-700" /> {t('dff.field.active', 'Active')}</label></div>
            </PersonalizedField>
          );
          if (udcForm.isUdcKey(fld.key)) return (
            <PersonalizedField componentKey="modal" fieldKey={fld.key} label={fld.label}
              readonlyChildren={udcForm.renderControl(fld.key, { disabled: true })}>
              {udcForm.renderControl(fld.key)}
            </PersonalizedField>
          );
          return null;
        }}
      />
      </div>
      <div className="mb-3 flex justify-end"><button onClick={() => void saveFlag()} disabled={busy} className="h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('dff.saving', 'Saving…') : t('dff.save_flag', 'Save Flag')}</button></div>

      {!isCreate && (
        <div className="border-t border-csm-border pt-2">
          <div className="mb-1 flex items-center gap-2"><span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('dff.section.targeting_rules', 'Targeting Rules (first match wins)')}</span>
            <button onClick={() => void addRule()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('dff.add_rule', '+ Rule')}</button></div>
          {rules.map((r, i) => (
            <div key={r.dff_rule_id} className="mb-1 flex flex-wrap items-center gap-1">
              <input type="number" value={r.priority} onChange={e => patch(i, { priority: Number(e.target.value) })} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].value))} className="h-7 w-12 rounded border border-csm-border px-1 text-csm-2xs" title={t('dff.tip.priority', 'priority')} />
              <div className="w-28">
                <FilteredModalSingleSelect
                  label={t('dff.field.target_workspace', 'Workspace')}
                  options={ws.map(w => ({ internal_code: String(w.une_workspace_id), display_text: w.name }))}
                  selectedCode={r.target_workspace_id == null ? null : String(r.target_workspace_id)}
                  onChange={(code) => {
                    const v = code ? Number(code) : null;
                    patch(i, { target_workspace_id: v });
                    void saveRule({ ...rules[i], target_workspace_id: v }, JSON.stringify(rules[i].value));
                  }}
                  clearLabel={t('dff.opt.any_ws', 'any ws')}
                />
              </div>
              <div className="w-28">
                <FilteredModalSingleSelect
                  label={t('dff.field.target_role', 'Role')}
                  options={roles.map(ro => ({ internal_code: String(ro.role_id), display_text: ro.internal_code }))}
                  selectedCode={r.target_role_id == null ? null : String(r.target_role_id)}
                  onChange={(code) => {
                    const v = code ? Number(code) : null;
                    patch(i, { target_role_id: v });
                    void saveRule({ ...rules[i], target_role_id: v }, JSON.stringify(rules[i].value));
                  }}
                  clearLabel={t('dff.opt.any_role', 'any role')}
                />
              </div>
              <input type="number" value={r.target_user_id ?? ''} onChange={e => patch(i, { target_user_id: e.target.value ? Number(e.target.value) : null })} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].value))} placeholder={t('dff.placeholder.user', 'user')} className="h-7 w-16 rounded border border-csm-border px-1 text-csm-2xs" />
              <input value={r.target_screen_code ?? ''} onChange={e => patch(i, { target_screen_code: e.target.value })} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].value))} placeholder={t('dff.placeholder.screen', 'screen')} className="h-7 w-24 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <input type="number" value={r.rollout_pct ?? ''} onChange={e => patch(i, { rollout_pct: e.target.value ? Number(e.target.value) : null })} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].value))} placeholder="%" className="h-7 w-12 rounded border border-csm-border px-1 text-csm-2xs" title={t('dff.tip.canary_pct', 'canary %')} />
              <input value={ruleValues[r.dff_rule_id] ?? JSON.stringify(r.value)} onChange={e => setRuleValues(prev => ({ ...prev, [r.dff_rule_id]: e.target.value }))} onBlur={e => void saveRule(rules[i], e.target.value)} placeholder={t('dff.placeholder.value', 'value')} className="h-7 w-24 rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('dff.tip.value_json', 'value JSON')} />
              <label className="flex items-center gap-1 text-csm-2xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => patch(i, { is_active: e.target.checked ? 'Y' : 'N' })} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].value))} /> {t('dff.on', 'on')}</label>
              <button onClick={() => void delRule(r.dff_rule_id)} className="text-csm-danger text-csm-2xs">{t('dff.del', 'del')}</button>
            </div>
          ))}
          <div className="mt-1 text-csm-2xs text-csm-watermark">{t('dff.hint.rules', 'Each rule: blank target = any. First active rule whose conditions all match wins; otherwise the flag default applies.')}</div>
        </div>
      )}
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
      {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('dff.hint.save_first', 'Save the flag first to add targeting rules.')}</div>}
    </Modal>

    {showDiscard && (
      <ConfirmModal
        title={t('dff.discard.title', 'Discard changes?')}
        message={t('dff.discard.message', 'You have unsaved changes that will be lost.')}
        confirmLabel={t('dff.discard.confirm', 'Discard')}
        cancelLabel={t('dff.discard.cancel', 'Keep editing')}
        danger={false}
        onConfirm={() => { reset(); onClose(); }}
        onCancel={() => setShowDiscard(false)}
      />
    )}
    </>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 rounded border border-csm-border bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <button onClick={onAdd} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('dff.add', '+ Add')}</button>
      </div>
      {children}
    </div>
  );
}
